import { Network, Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { DocumentTimestampDetails } from '@/components/identities/DocumentTimestampDetails'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import type { FaceMatchingReference } from '@/services/api/face-matching'
import type { FacesCardActions, FacesCardState } from './FacesCard'
import { FaceTile } from './FaceTile'
import { qualityFlagName } from './format'

interface Props {
  reference: FaceMatchingReference
  state: FacesCardState
  actions: FacesCardActions
}
const BASE = 'identity.faces'

export function FaceGalleryItem(props: Props) {
  return (
    <li className="flex min-w-0 flex-col gap-3 rounded-md border border-border bg-card p-3">
      <FaceSource reference={props.reference} state={props.state} />
      <FaceActions {...props} />
    </li>
  )
}

function FaceSource({ reference, state }: Pick<Props, 'reference' | 'state'>) {
  const { t } = useTranslation()
  return (
    <div className="flex items-start gap-2">
      <FaceTile
        url={reference.crop_url}
        alt={reference.document_name ?? t(`${BASE}.title`)}
        size="lg"
      />
      <div className="flex min-w-0 flex-col gap-1">
        <span className="truncate text-xs text-muted-foreground">
          {reference.document_name ?? t(`${BASE}.unknownSource`)}
        </span>
        <DocumentTimestampDetails
          uploadedAt={reference.uploaded_at}
          processedAt={reference.processed_at}
        />
        {reference.quality_flags?.length ? (
          <div className="flex flex-wrap gap-1">
            {reference.quality_flags.map(flag => (
              <Badge key={flag} variant="warning">
                {qualityFlagName(flag, t)}
              </Badge>
            ))}
          </div>
        ) : null}
        {state.mainFaceId === reference.face_id ? (
          <Badge variant="success">{t(`${BASE}.mainPhoto`)}</Badge>
        ) : null}
      </div>
    </div>
  )
}

function FaceActions({ reference, state, actions }: Props) {
  const { t } = useTranslation()
  const pending = state.pendingFaceId === reference.face_id
  const { onToggleReference, onMakeMainPhoto } = actions
  return (
    <>
      <label className="flex min-w-0 items-center gap-2 text-2xs text-muted-foreground max-md:min-h-9">
        {/* Without the callback (a viewer) the switch still shows whether the
            face is a reference; it just cannot be flipped. */}
        <Switch
          size="sm"
          checked={reference.reference}
          disabled={pending || !onToggleReference}
          aria-label={t(`${BASE}.actions.useAsReference`)}
          data-face-id={reference.face_id}
          onCheckedChange={next => onToggleReference?.(reference.face_id, next)}
        />
        {t(`${BASE}.actions.useAsReference`)}
      </label>
      {onMakeMainPhoto ? (
        <Button
          variant="outline"
          size="sm"
          disabled={pending || state.mainFaceId === reference.face_id}
          onClick={() => onMakeMainPhoto(reference.face_id)}
          className="h-auto min-h-8 w-full min-w-0 whitespace-normal text-center"
        >
          <Star className="size-3.5" />
          {t(`${BASE}.actions.makeMainPhoto`)}
        </Button>
      ) : null}
      <Button
        variant="outline"
        size="sm"
        disabled={pending}
        onClick={() => actions.onShowInGraph(reference.face_id)}
      >
        <Network className="size-3.5" />
        {t(`${BASE}.actions.showInGraph`)}
      </Button>
    </>
  )
}
